import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { showError } from '@utils';
import { QUERY_KEYS } from 'constants/queryKeys';
import { reservationServices } from '@services';
import { useQuery } from 'react-query';
import { useAssignees } from 'hooks/common';
import { useSelector } from 'react-redux';
import { selectReservationsPinnedFilters } from '@store';
import {
  normalizeReservations,
  normalizeReservationsAgents,
  normalizeReservationsEnums,
  getParamsAndPayloadReservations,
} from './utils';
import { RESERVATIONS_PAGE_SIZE } from './constants';

export const useReservationsOperations = (enableFetch = true) => {
  const router = useRouter();
  const { assignees, isFetching: isAssigneesFetching } = useAssignees();

  const {
    page = 1,
    pageSize = RESERVATIONS_PAGE_SIZE,
    orderBy = 'createdAt',
    ...others
  } = router.query;
  const allFilters = router.query;

  const pinnedFilters = useSelector(selectReservationsPinnedFilters);

  const {
    params: cleanedParams,
    payload,
    urlParams,
  } = useMemo(
    () =>
      getParamsAndPayloadReservations({
        allFilters,
        pageSize,
        page,
        orderBy,
      }),
    [allFilters, pageSize, page, orderBy]
  );

  const reservationsEnumsResponse = useQuery(
    QUERY_KEYS.GET_RESERVATIONS_ENUMS_KEY,
    () => reservationServices.getReservationsEnums()
  );

  const reservationsEnums = normalizeReservationsEnums(
    reservationsEnumsResponse?.data?.data
  );

  const reservationsResponse = useQuery(
    [QUERY_KEYS.GET_RESERVATIONS_KEY, cleanedParams],
    () => {
      return reservationServices.getReservations({
        urlParams,
        payload,
      });
    },
    {
      enabled: enableFetch && reservationsEnumsResponse.isFetched,
      refetchOnMount: true,
    }
  );

  const reservations = normalizeReservations(
    reservationsResponse?.data?.data,
    reservationsEnums
  );

  const reservationsAgents = normalizeReservationsAgents(assignees);

  const hasFilters =
    pinnedFilters?.length > 0 || Object.keys(others).length > 0;

  useEffect(() => {
    if (reservationsResponse.isError) {
      showError(reservationsResponse?.error);
    }
  }, [reservationsResponse.isError]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    page,
    total: reservationsResponse?.data?.meta?.total,
    loading:
      reservationsResponse?.isFetching ||
      reservationsEnumsResponse?.isFetching ||
      isAssigneesFetching,
    agentsOptions: reservationsAgents,
    filters: allFilters,
    reservations,
    reservationsEnums,
    pageSize: parseInt(pageSize, 10) || RESERVATIONS_PAGE_SIZE,
    hasErrors: reservationsResponse?.isError,
    errorMessage: reservationsResponse?.error,
    reservationsQueryParams: urlParams,
    reservationsFilterQueryPayload: payload,
    getReservations: reservationsResponse.refetch,
    hasFilters,
  };
};
